import React from 'react';
import { useSelector } from 'react-redux';
import type { NextPage } from 'next';

import { shopItems } from '@r1-frontend/api-domru/api-content/v1/shop/items';
import { EExpandShopItems, EShopCategory } from '@r1-frontend/api-domru/api-content/v1/shop/items/requestDto';
import { availability, EAvailability, IAvailability } from '@r1-frontend/api-domru/api-loyalty/v1/program/availability';

import { EPlaces, getFaqArticles, IFaqArticle } from '~/src/api/content/faq';
import { getCityInfo } from '~/src/api/content/geography/get-city-info';
import { IShopItem } from '~/src/api/content/shop/getShopItems';
import Layout from '~/src/features/_layout';
import DomruBonus from '~/src/features/domru-bonus';
import { initialProps } from '~/src/helpers/basePageFunc';
import getRequester from '~/src/helpers/getRequester';
import { nonPermanentRedirect } from '~/src/helpers/url';
import { getCurrentCity, selectCompanyName } from '~/src/store/selectors/city';

interface IProps {
    loyaltyIsAvailable?: boolean,
    loyaltyIsRegistered?: boolean,
    partners?: IShopItem[],
    faq?: IFaqArticle[],
}

const DomruBonusPage: NextPage<IProps> = ({
    loyaltyIsAvailable,
    loyaltyIsRegistered,
    partners = [],
    faq,
}) => {
    const currentCity = useSelector(getCurrentCity);
    const companyName = useSelector(selectCompanyName);

    const cashback = currentCity.loyaltyProgramCashback;

    return (
        <>
            <Layout
                title={`Программа лояльности ${companyName} с бонусами в ${currentCity.cityIn}`}
                description={`Участник программы получает кешбэк в виде бонусов в размере 3% от Абонентской платы. Бонусами можно оплатить до 100% стоимости товаров или услуг ${companyName}. Звоните по ☎ ${currentCity.phone}`}
                withMenu={true}
                withChat={true}
                withFooter={false}
            >
                <DomruBonus
                    partners={partners}
                    loyaltyIsRegistered={!!loyaltyIsRegistered}
                    loyaltyIsAvailable={!!loyaltyIsAvailable}
                    cashback={`${cashback}%`}
                    faq={faq}
                    companyName={companyName}
                />
            </Layout>
        </>
    );
};

DomruBonusPage.getInitialProps = async(options) => {
    const { validatedAccessToken } = await initialProps(options, { isNeedAuthData: true });
    const { res, store } = options;
    const state = store.getState();
    const { city: { currentCity: city, provider: { providerId } } } = state;
    const doRequest = getRequester(options.req);

    const respPl = await getCityInfo({ providerId, filter: ['pl_available'], active: false });
    const isAvailableOnCity = (respPl.ok && respPl.data?.pl_available) ? !!respPl.data?.pl_available : false;

    const [faqData] = await Promise.all([
        getFaqArticles({
            places: [EPlaces.Loyalty],
            providerId,
        }),
    ]);
    if (!isAvailableOnCity) {
        nonPermanentRedirect({ res, location: validatedAccessToken ? '/lk' : '/' });
    } else {
        const partners =
            await doRequest.apiContent<{ items: IShopItem[] }>(
                shopItems(
                    providerId,
                    validatedAccessToken ?? '',
                    [EShopCategory.LoyaltyProgram],
                    [EExpandShopItems.Category, EExpandShopItems.Advertising],
                ),
            );

        if (city.plAvailable) {
            const result: IProps = {
                partners: partners.isSuccess ? partners.payload.items : undefined,
                faq: faqData.items,
            };

            if (validatedAccessToken) {
                const loyaltyAvailableResp = await doRequest.apiLoyalty<IAvailability>(
                    availability(providerId),
                );

                if (loyaltyAvailableResp?.isSuccess) {
                    const { payload: { available } = {} } = loyaltyAvailableResp;

                    result.loyaltyIsAvailable = available === EAvailability.AVAILABLE;
                    result.loyaltyIsRegistered = available === EAvailability.REGISTRED;
                }
            }

            return result;
        } else {
            return ({
                partners: partners.isSuccess ? partners.payload.items : undefined,
                faq: faqData.items,
            });
        }
    }

    return ({
        faq: faqData.items,
    });
};

export default DomruBonusPage;
